import { useCallback, useEffect, useMemo, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { GET_ALL_HOUSES } from "../graphqlQueries";
import { useRecoilValue } from "recoil";
import { useAtom } from "jotai";

import useGetFacetsData from "./useGetFacetsData";
import useCountries from "./useCountries";
import { cmsStaticPropertyIds, deepClone, modelingCategoriesHierarchy } from "../utils";
import { housesState, organizationState, userState } from "../atoms";
import { TYPES, defaultMatchBarState, defaultVisbileParameters, fieldsUnderProperties, getFields, importanceRanksOptions, lodOptions, sustainabilityPillarsOptions } from "../components/master-search/utils";
import usePropertiesNew from "./usePropertiesNew";


export default function useMatchBarNew(hookArgs = {}) {

    const { visibileParameters = defaultVisbileParameters, type, parameterSettings = {}, otherFields = [], defaultOrganizationIds } = hookArgs

    // API
    const [getAllHouses] = useLazyQuery(GET_ALL_HOUSES);

    // global states
    const [houses, setHouses] = useAtom(housesState);
    const { id: orgId, displayName: orgName } = useRecoilValue(organizationState);
    const userData = useRecoilValue(userState);

    const defaultMatchBarData = useMemo(() => ({ ...defaultMatchBarState, organizations: defaultOrganizationIds || [orgId] }), [orgId, defaultOrganizationIds]) // To set by default multiple orgIds need to pass defaultOrganizationIds

    // local state
    const [matchBarData, setMatchBarData] = useState(defaultMatchBarData)

    // hooks
    const { brands, distributors, categories: allCategories } = useGetFacetsData();

    const {
        getMatchbarData,
        getOptionsByPropertyId,
    } = usePropertiesNew(orgId);

    const countries = useCountries()

    // const parenCategories = useMemo(() => allCategories.filter((category) => !category.parentCategoryId), [allCategories])

    const categoriesHeirarchy = useMemo(() => modelingCategoriesHierarchy(allCategories), [allCategories])

    // sub categories in categorised heirarchy format 
    // const subCategories = useMemo(() => filteredSubCatAccToParentCat(allCategories, matchBarData?.[TYPES.CATEGORIES] || []), [allCategories, matchBarData])

    // fetching houses/projects list if not found in global storage
    useEffect(() => {
        if (!houses && orgId && visibileParameters?.[TYPES.PIRS]) {

            async function getOrgProjects() {
                const { data: housesData } = await getAllHouses();
                const allHouses = housesData?.getAllHouses || [];
                setHouses([...allHouses]);
            }
            getOrgProjects();
        }
    }, [getAllHouses, houses, setHouses, orgId, visibileParameters]);

    // 
    const organizations = useMemo(() => {
            return (Array.isArray(userData?.organizations) ? userData.organizations : [])
    }, [userData]);

    const visibleFields = useMemo(() => getFields({ visibileParameters, parameterSettings, otherFields }), [visibileParameters, parameterSettings, otherFields])


    // a common function to be used for discipline and tags option population
    const getDisciplineOrTagsCommon = useCallback((propertyId) => {
        const selectedOrganizationIds = matchBarData[TYPES.ORGANIZATIONS] ? [...matchBarData[TYPES.ORGANIZATIONS]] : [{
            id: orgId,
            displayName: orgName,
        }]

        function getBucketData(organizationId) {
            const bucketData = getOptionsByPropertyId({ propertyId, organizationId}).map((option) => {
                // combining ids to avoid duplication
                const id = `${option.id}:${organizationId}`
                return { ...option, id, organizationId };
            })

            return bucketData
        }

        //non Filtered Options type multiselection
        let options = getBucketData(orgId)
        
        // filtered option type categorised selection
        if (visibileParameters[TYPES.ORGANIZATIONS]) {
            options = selectedOrganizationIds.map((organizationId) => ({
                id: organizationId,
                displayName: organizations.find((organization) => organization.id === organizationId)?.displayName,
                buckets: getBucketData(organizationId)
            }))
        }

        return options

    }, [matchBarData, orgId, orgName, organizations, visibileParameters, getOptionsByPropertyId])

    const disciplines = useMemo(() => getDisciplineOrTagsCommon(cmsStaticPropertyIds.disciplines), [getDisciplineOrTagsCommon])
    const tags = useMemo(() => getDisciplineOrTagsCommon(cmsStaticPropertyIds.searchTags), [getDisciplineOrTagsCommon])


    // handling on change of all params 
    const onChange = useCallback((changedData, type) => {
        let updatedMatchBarData = deepClone(matchBarData)
        updatedMatchBarData[type] = changedData
        setMatchBarData(updatedMatchBarData)
        return updatedMatchBarData
    }, [matchBarData])


    // to get options for field types propertyKeys & propertyWithValues
    const getOptionsOfPropertyOptionsAndValueOptions = useCallback(() => {

        const removeParameterDisplaySections = () => {
            if (type === "public-catalog") {
                // rfa section id
                return ["11ca7fb7-4037-4d41-aef4-82a4bd26a1d7"];
            }
            return [];
        };

        const options = getMatchbarData(matchBarData[TYPES.CATEGORIES], matchBarData[TYPES.PROPERTY_KEYS], removeParameterDisplaySections())

        return options
    }, [getMatchbarData, matchBarData, type])

    // going to output
    const facets = useCallback((matchBarData) => {
        const facetsData = [];

        if (visibileParameters?.[TYPES.BRANDS_AND_DISTRIBUTORS] && matchBarData?.[TYPES.BRANDS_AND_DISTRIBUTORS].length) {

            function pushFacetsData(brandsOrDistributorsOptions, type) {
                // common function to set facet data for brands and distributors
                const brandsOrDistributorsFacets = brandsOrDistributorsOptions.filter((brandOrDistributor) => matchBarData?.[TYPES.BRANDS_AND_DISTRIBUTORS].includes(brandOrDistributor.id)).map((brandOrDistributor) => brandOrDistributor.id);
                if (brandsOrDistributorsFacets.length) {
                    facetsData.push({
                        id: type,
                        values: brandsOrDistributorsFacets,
                    });
                }
            }

            pushFacetsData(brands, 'brands')
            pushFacetsData(distributors, 'distributors')

        }

        if (matchBarData?.[TYPES.CATEGORIES].length) {
            facetsData.push({
                id: "categories",
                values: matchBarData[TYPES.CATEGORIES],
            });
        }

        return facetsData;
    }, [brands, distributors, visibileParameters]);

    // NOTE: these keys are according to what frontend supports 
    const optionsOutput = useMemo(() => {

        const brandsAndDistributors = [
            {
                id: "brand",
                displayName: "Brand",
                buckets: brands,
            },
            {
                id: "distributor",
                displayName: "Distributors",
                buckets: distributors,
            },
        ]

        return ({
            disciplines,
            tags,
            organizations,
            // categories here consists both parent and sub categories
            categories: categoriesHeirarchy,
            countries,
            houses,
            brands,
            distributors,
            brandsAndDistributors,
            propertyKeys: getOptionsOfPropertyOptionsAndValueOptions().optionsForProperties,
            propertyWithValues: getOptionsOfPropertyOptionsAndValueOptions().optionsForPropertyValues,
            lod: lodOptions,
            importanceRanks: importanceRanksOptions,
            sustainabilityPillars: sustainabilityPillarsOptions
        })

    }, [brands, countries, disciplines, distributors, getOptionsOfPropertyOptionsAndValueOptions, houses, organizations, categoriesHeirarchy, tags])


    const getPropertiesFieldData = useCallback((matchBarData) => {
        const propertiesData = [];

        fieldsUnderProperties.forEach(({ type, id }) => {
          
            if (matchBarData?.[type]?.length) {
                const splitIds = matchBarData[type].map(combinedIds => combinedIds.split(":")[0])
                propertiesData.push({
                    id,
                    values: [...new Set(splitIds)],
                });
            }
        })

        if (matchBarData?.[TYPES.PROPERTY_KEYS]?.length) {

            const propertyKeysValueData = matchBarData?.[TYPES.PROPERTY_WITH_VALUES]?.length ? [...matchBarData[TYPES.PROPERTY_WITH_VALUES]] : []
            matchBarData[TYPES.PROPERTY_KEYS].forEach((propertyKeyId) => {

                const propertyKeyData = { id: propertyKeyId }
                if (propertyKeysValueData.length) {
                    const allValuesIds = []

                    // Note: To use below commented code if not combined two ids to differentiate in UI
                    // geting all value options from property values options data for this particular property key and then filtering out selected vaules who comes under this property key
                    // const allValuesIds = optionsOutput[TYPES.PROPERTY_WITH_VALUES].find(propertyKeyOptionData => propertyKeyOptionData.id === propertyKeyId)?.buckets.filter(propertyValueData => propertyKeysValueData.includes(propertyValueData.id))?.map(data => data.id) || []

                    propertyKeysValueData.forEach(propertyKeyAndPropertyValueIds => {
                        // have kept combination of ids for property value to remove UI selection conflict for same property value in diff property key section
                        const splitOfIds = propertyKeyAndPropertyValueIds.split(":")
                        const propertyKeyIdFromSplit = splitOfIds[1]
                        const propertyValueIdFromSplit = splitOfIds[0]

                        if (propertyKeyId === propertyKeyIdFromSplit) {
                            allValuesIds.push(propertyValueIdFromSplit)
                        }
                    })

                    if (allValuesIds.length) {
                        propertyKeyData.values = allValuesIds
                    }
                }
                propertiesData.push(propertyKeyData)

            });
        }

        return propertiesData
    }, [])

    const onResetAll = useCallback(() => {
        setMatchBarData(defaultMatchBarData)
    }, [defaultMatchBarData])

    const getMatchBarOutput = useCallback((outsideHookMatchbarData, onlyVisibleParameters = false) => {
        const finalMatchBarData = outsideHookMatchbarData ? { ...defaultMatchBarData, ...outsideHookMatchbarData } : matchBarData;
        const backendFormatOutput = {};
        const dynamicFieldNames = visibleFields.filter(fieldData => fieldData.dynamic).map(fieldData => fieldData.id);

        // Helper function to check visibility and field length
        const shouldIncludeField = (type, excludeDynamic = true) => ((!onlyVisibleParameters || visibileParameters?.[type]) && (!excludeDynamic || !dynamicFieldNames.includes(type)));

        // Process all mapped fields
        Object.entries(fieldMappings).forEach(([type, { key, transform }]) => {
            if (shouldIncludeField(type)) {
                backendFormatOutput[key] = transform(finalMatchBarData[type]);
            }
        });

        // Process facets
        if (shouldIncludeField(TYPES.BRANDS_AND_DISTRIBUTORS)) {
            backendFormatOutput.facets = facets(finalMatchBarData);
        }

        // Process properties
        const hasVisiblePropertyFields = fieldsUnderProperties.some(field => shouldIncludeField(field.type));
        if (hasVisiblePropertyFields) {
            backendFormatOutput.properties = getPropertiesFieldData(finalMatchBarData);
        }

        //Process other fields
        dynamicFieldNames.forEach(fieldName => {
            if (shouldIncludeField(fieldName, false)) {
                backendFormatOutput[fieldName] = finalMatchBarData[fieldName]
            }
        })

        return { output: finalMatchBarData, backendFormatOutput };
    }, [facets, matchBarData, getPropertiesFieldData, defaultMatchBarData, visibileParameters, visibleFields]);

    return ({ getMatchBarOutput, options: optionsOutput, onChange, onResetAll, setMatchBarData })
}

// Mapping of frontend to backend fields with their transformation rules
const fieldMappings = {
    [TYPES.COUNTRIES]: { key: 'countries', transform: (val) => val || [] },
    [TYPES.TEXT]: { key: 'text', transform: (val) => val.length >= 3 ? val : '' },
    [TYPES.NAME]: { key: 'displayName', transform: (val) => val.length >= 3 ? val : '' },
    [TYPES.DESCRIPTION]: { key: 'description', transform: (val) => val.length >= 3 ? val : '' },
    [TYPES.SERIAL_NUMBER]: { key: 'serialNumber', transform: (val) => val.length >= 3 ? val : '' },
    [TYPES.SUSTAINABILITY_PILLARS]: { key: 'bimmatchStandard', transform: (val) => val },
    [TYPES.BIM_FILE]: { key: 'bimFile', transform: (val) => !!val },
    [TYPES.GWP]: { key: 'gwp', transform: (val) => !!val },
    [TYPES.ORGANIZATIONS]: { key: 'organizationIds', transform: (val) => val },
    [TYPES.PIRS]: { key: 'houses', transform: (val) => val || [] },
    [TYPES.ROLES]: { key: 'responsibilityRoles', transform: (val) => val || [] },
    [TYPES.KEY_WORDS]: { key: 'sectionKeywords', transform: (val) => val || [] },
    [TYPES.IMPORTANCE_RANKS]: { key: 'importance', transform: (val) => val || [] },
    [TYPES.CATEGORIES]: { key: 'categories', transform: (val) => val || [] },
    [TYPES.BRANDS_AND_DISTRIBUTORS]: { key: 'brandsAndDistributors', transform: (val) => val || [] },
    [TYPES.PROPERTY_KEYS]: { key: 'propertyKeys', transform: (val) => val || [] },
    [TYPES.PROPERTY_WITH_VALUES]: { key: 'propertyWithValues', transform: (val) => val || [] },
    [TYPES.MATERIALS]: { key: 'materials', transform: (val) => val || [] },
    [TYPES.LOD]: { key: 'lod', transform: (val) => val || '' }
};

// const getUpdatedSubCategory = (changedParentCatIds, oldSelectedSubCat, allCategories) => {
//     let updatedSubCat = []

//     if (!oldSelectedSubCat?.length) {
//         return updatedSubCat
//     }

//     const updatedSubCategories = filteredSubCatAccToParentCat(allCategories, changedParentCatIds)
//     const subCatIds = updatedSubCategories.length ? updatedSubCategories.map(parentCategory => parentCategory.buckets).flat().map(subCategory => subCategory.id) : []
//     updatedSubCat = oldSelectedSubCat.filter(subCatId => subCatIds.includes(subCatId))
//     return updatedSubCat
// }

